import { useMemo } from 'react';
// Note: These functions are available in the context but not directly used in this hook
// import { calcProPercentsBasedOnFilterAndDays, getPercentageByTotal, itemPercentage, projectionPerMonth } from '../utilis/common';

/**
 * Custom hook for AI calculation functions and business logic
 * Provides calculation functions, filter business logic, and card interaction details
 */
export const useAICalculationContext = ({
  filter,
  transferType,
  projectionDays,
  cardFilter,
  percentageBy,
  lockedTotalBy,
  specialComparisonSet,
  filterTotal,
  percentageAgainst
}) => {
  return useMemo(() => {
    // CRITICAL: Include ALL calculation function source code for AI understanding
    const calculationFunctions = {
      calcProPercentsBasedOnFilterAndDays: {
        description: "Scales values and percentages based on filter date range and projection days",
        sourceCode: `
        export const calcProPercentsBasedOnFilterAndDays = (value, filter, projectionDays, isPercentage, scale) => {
          if ((scale && isPercentage) || !isPercentage) {
            const days = getDaysBetweenDates(filter);
            const projectionDaysParsed = projectionDays === 0 || !projectionDays ? days : projectionDays;
            const scalingFactor = projectionDaysParsed / days;
            if (filter?.transferType === OVERALL_PAGE_SUB_TYPE.TOTAL && isPercentage) {
              const scaledValue = Number(value) * scalingFactor;
              const result = scaledValue < 1 ? scaledValue?.toFixed(1) || scaledValue : Math.round(scaledValue);
              return result > 0 ? \`+\${result}\` : result;
            }
            if (filter?.transferType === OVERALL_PAGE_SUB_TYPE.TOTAL && !isPercentage) {
              return value > 0 ? \`+\${value}\` : value;
            }
            const resCount = (Number(value) * scalingFactor) && (Number(value) * scalingFactor) < 1 ? (Number(value) * scalingFactor)?.toFixed(1) || (Number(value) * scalingFactor) : Math.round(Number(value) * scalingFactor);
            return resCount;
          } else {
            return value;
          }
        };`,
        usage: "Used to scale all values and percentages in cards based on selected date range and projection settings"
      },
      getPercentageByTotal: {
        description: "Determines the correct percentage base (census average, bed capacity, or custom percentage)",
        sourceCode: `
        export const getPercentageByTotal = (total, pageType = null) => {
          const { selectedAccount } = store.getState().common;
          if (selectedAccount && selectedAccount?.percentageBy === ACCOUNT_PERCENTAGE_BY.BED) {
            return total?.bedCapacity;
          } else {
            if (pageType === PAGE_TYPE.HOSPITAL) {
              const { percentageAgainst } = store.getState().hospital;
              return percentageAgainst;
            } else if (pageType === PAGE_TYPE.COMMUNITY_TRANSFER) {
              const { percentageAgainst } = store.getState().communityTransfer;
              return percentageAgainst;
            } else {
              return total?.censusAverage;
            }
          }
        };`,
        usage: "Determines what value to use as the denominator for percentage calculations"
      },
      projectionPerMonth: {
        description: "Calculates projected monthly values based on current data and date range",
        sourceCode: `
        export function projectionPerMonth(total, filter, projectionDays = 30) {
          let projections = 0;
          const { startDate, endDate } = filter;
          var a = moment.utc(startDate).startOf("day");
          var b = moment.utc(endDate).endOf("day");
          let days = b.diff(a, "days") + 1;
          if (days <= 30) {
            projections = (projectionDays * total) / days;
          } else {
            const months = days / projectionDays;
            projections = total / months;
          }
          return projections ? Math.round(projections) : 0;
        }`,
        usage: "Projects current values to monthly estimates based on date range"
      },
      itemPercentage: {
        description: "Calculates percentage of item relative to a base value",
        sourceCode: `
        export function itemPercentage(total, mainTotalVal = null, type, page = "hospital", forComparison) {
          let selectedTotal = 0;
          if (!mainTotalVal) {
            if (page === PAGE_TYPE.HOSPITAL) {
              const { mainTotal } = store.getState().hospital;
              selectedTotal = mainTotal;
            }
            // ... other page types
          } else {
            selectedTotal = mainTotalVal;
          }
          let percentage = 0;
          if (total !== 0 && selectedTotal !== 0) {
            percentage = (total / selectedTotal) * 100;
          }
          return type === "number" ? percentage.toFixed(2) : \`\${percentage.toFixed(2)}%\`;
        }`,
        usage: "Calculates percentages for individual items in cards"
      }
    };

    // CRITICAL: Include ALL filter business logic from hospital-common.js
    const filterBusinessLogic = {
      updateFilterListData: {
        description: "Main function that processes all card filters and updates card data accordingly",
        keySteps: [
          "1. Determine base patient data (all, planned, or unplanned transfers)",
          "2. Process priority filters in sequence - first filter affects percentage base for subsequent filters",
          "3. Apply each card filter and update patient data accordingly",
          "4. Calculate new percentage bases for remaining cards",
          "5. Update facility-wise percentages",
          "6. Handle locked totals and census comparisons"
        ],
        priorityFilterLogic: `
        Priority filters work as follows:
        - When user clicks checkbox on any card item, it becomes a priority filter
        - First priority filter uses original percentage base (census/bed capacity)
        - Subsequent priority filters use the filtered patient count as percentage base
        - Example: If "New Hospitalizations" (7 patients) is first filter, 
          then Insurance card percentages use 7 as denominator instead of total transfers (9)
        - This cascading effect explains why percentages change across all cards when filtering
        `,
        transferTypeImpact: `
        Transfer type selection affects all calculations:
        - PLANNED: Filters patientData to only plannedHospitalTransfer patients
        - UNPLANNED: Filters patientData to only unplannedHospitalTransfer patients  
        - ALL: Shows combined data with special handling
        - When transfer type is selected, percentage base changes to filtered count
        `,
        lockedTotalsLogic: `
        Locked totals allow fixing percentage calculations:
        - Census Lock: Uses census data as fixed percentage base
        - Transfer Type Lock: Uses specific transfer type count as base
        - Priority Filter Lock: First applied filter becomes permanent base
        - Facility Lock: Calculates percentages per facility
        `
      },
      cardFilterFunctions: {
        hospitalizationsFilter: "Filters by reHospitalization field based on New/Re-Hospitalizations selection",
        DCERDataFilter: "Filters by wasAdmitted field (true for DC, false for ER)",
        returnsDataFilter: "Filters by wasReturned field for Returned/Didn't Return",
        hospitalDataFilter: "Filters by hospital IDs from hospital selection",
        ninetyDaysDataFilter: "Filters by 90-day analysis time ranges"
      },
      cardDataFunctions: {
        hospitalizationData: "Processes hospitalizations into New/Re-Hospitalizations with percentages",
        dcErData: "Separates patients into DC (admitted) vs ER (not admitted) with percentages",
        insuranceData: "Groups patients by insurance type with percentages",
        returnsData: "Calculates returned vs didn't return statistics",
        ninetyDaysDataList: "Processes 90-day analysis data with time-based groupings",
        floorsData: "Groups transfers by floor/unit",
        doctorData: "Groups transfers by attending doctor",
        daysData: "Groups transfers by day of week",
        dxData: "Groups transfers by diagnosis codes",
        shiftData: "Groups transfers by shift (Morning/Evening/Night)",
        nurseData: "Groups transfers by nurse",
        hospitalDataList: "Processes hospital destination data"
      }
    };
    
    // CRITICAL: Card Interaction and Checkbox Functionality Details
    const cardInteractionDetails = {
      checkboxFunctionality: {
        description: "Each card item has a checkbox that acts as a filter when clicked",
        handleToggle: {
          description: "Main function called when user clicks any checkbox in any card",
          process: [
            "1. Identifies the item, card type, and current checkbox state",
            "2. Updates cardFilter state to add/remove the item ID",
            "3. Calls priorityFilter() to update priority filter sequence",
            "4. Calls updateFilterListData() to recalculate all card data",
            "5. Updates both main and comparison data if comparison is active"
          ],
          codeExample: `
          // When user clicks checkbox on "New Hospitalizations" item:
          handleToggle({
            item: { _id: "newHosp", patientIds: [1,2,3,4,5,6,7] },
            type: "hospitalizations",
            cardTitle: "New Hospitalizations & Re-Hospitalizations"
          })
          // This triggers priority filter system and updates all other cards
          `
        },
        handleToggleAll: {
          description: "Toggles all items in a card on/off at once",
          usage: "Used by cards with many items like Insurance, Floors, Doctors, etc."
        },
        priorityFilterSystem: {
          description: "Critical system that determines how percentages are calculated across cards",
          rules: [
            "First checkbox clicked becomes priority filter #1",
            "Second checkbox clicked becomes priority filter #2, etc.",
            "Priority filters change percentage denominator for all subsequent cards",
            "Removing a priority filter recalculates all percentage bases",
            "Priority order affects which patient data is used for calculations"
          ],
          example: `
          Scenario: Total transfers = 9, New Hospitalizations = 7
          1. User clicks "New Hospitalizations" checkbox
          2. This becomes priority filter #1 
          3. Now Insurance card percentages use 7 as denominator instead of 9
          4. If Medicare = 5 patients, percentage becomes 5/7 = 71% instead of 5/9 = 56%
          5. This explains why all card percentages change when filtering
          `
        }
      },
      cardSpecificBehaviors: {
        hospitalizations: {
          items: ["New Hospitalizations", "Re-Hospitalizations"],
          filterField: "reHospitalization",
          specialLogic: "When only one item selected, filters patient data by reHospitalization field"
        },
        dcer: {
          items: ["DC (Discharged/Admitted)", "ER (Emergency Room/Not Admitted)"],
          filterField: "wasAdmitted",
          specialLogic: "DC = wasAdmitted:true, ER = wasAdmitted:false",
          terminology: "CRITICAL: DC means DISCHARGED (admitted patients), ER means emergency visits without admission"
        },
        insurance: {
          items: "Dynamic list of insurance types from patient data",
          filterField: "insuranceId",
          features: ["Searchable", "Toggle all", "Shows top insurances first"]
        },
        returns: {
          items: ["Returned", "Didn't Return"],
          filterField: "wasReturned",
          specialLogic: "Uses different percentage base (percentageAgainst) than other cards"
        },
        ninetyDays: {
          items: ["1-7 Day", "8-14 Day", "15-30 Day", "31-60 Day", "61-90 Day"],
          filterField: "Time ranges based on 90-day analysis data",
          specialLogic: "Complex filtering through ninetyDaysDataFilter function"
        },
        floors: {
          items: "Dynamic list of floor/unit names",
          filterField: "floorId",
          features: ["Minimizable", "Tagged list display", "Searchable"]
        },
        doctors: {
          items: "Dynamic list of doctor names",
          filterField: "doctorId", 
          features: ["Minimizable", "Tagged list display", "Searchable", "Shows top doctors"]
        },
        days: {
          items: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
          filterField: "day",
          features: ["Visual day-of-week display", "Minimizable"]
        },
        diagnosis: {
          items: "Dynamic list of diagnosis codes/descriptions",
          filterField: "dxIds",
          specialLogic: "Filters by intersection of dxIds array",
          features: ["Minimizable", "Tagged list display", "Searchable"]
        },
        shifts: {
          items: ["Morning", "Evening", "Night"],
          filterField: "shiftName",
          features: ["Visual shift display", "Minimizable"]
        },
        nurses: {
          items: "Dynamic list of nurse names",
          filterField: "nurseId",
          features: ["Minimizable", "Tagged list display", "Searchable"]
        },
        hospitals: {
          items: "Dynamic list of hospital destinations",
          filterField: "hospitalId",
          specialLogic: "Uses different percentage base (percentageAgainst) like returns card",
          features: ["Slider display", "Minimizable", "Searchable"]
        }
      },
      dataFlow: {
        description: "Complete data flow when user interacts with checkboxes",
        steps: [
          "1. User clicks checkbox on any card item",
          "2. handleToggle() called with item details",
          "3. cardFilter state updated to include/exclude item ID",
          "4. priorityFilter() updates priority filter sequence",
          "5. updateFilterListData() called with new cardFilter",
          "6. Patient data filtered based on priority filter sequence",
          "7. Each card's data recalculated with new patient subset",
          "8. Percentages recalculated using new base values",
          "9. All cards re-render with updated data and percentages",
          "10. If comparison mode active, same process runs for comparison data"
        ],
        criticalPoints: [
          "Priority filter order matters - first filter has different percentage base",
          "Each filter reduces the patient dataset for subsequent calculations",
          "Percentage bases change dynamically based on active filters",
          "Some cards (returns, hospitals) use different percentage calculation methods",
          "Transfer type selection affects all card calculations"
        ]
      }
    };

    // CRITICAL: Real-world Examples and Edge Cases for AI Understanding
    const realWorldExamples = {
      basicFiltering: {
        scenario: "User clicks 'New Hospitalizations' checkbox",
        before: {
          totalTransfers: 9,
          newHospitalizations: 7,
          medicare: 5,
          medicarePercentage: "5/9 = 56%"
        },
        after: {
          priorityFilter: "New Hospitalizations becomes priority filter #1",
          filteredPatients: 7,
          medicarePercentage: "5/7 = 71%",
          explanation: "All other card percentages now use 7 as denominator"
        }
      },
      multipleFilters: {
        scenario: "User clicks 'New Hospitalizations' then 'Medicare'",
        step1: {
          action: "Click New Hospitalizations",
          result: "7 patients, becomes priority filter #1"
        },
        step2: {
          action: "Click Medicare", 
          result: "5 patients (subset of 7), becomes priority filter #2"
        },
        finalState: {
          filteredPatients: 5,
          percentageBase: "All remaining cards use 5 as denominator",
          explanation: "Each filter narrows the dataset for subsequent calculations"
        }
      },
      transferTypeImpact: {
        scenario: "User selects 'Planned Transfers' then applies filters",
        before: {
          totalTransfers: 9,
          plannedTransfers: 4,
          unplannedTransfers: 5
        },
        afterTransferTypeSelection: {
          activePatients: 4,
          percentageBase: "4 (only planned transfers)",
          cardDataRecalculated: "All cards now show data for planned transfers only"
        },
        afterAdditionalFiltering: {
          explanation: "If user then clicks card checkboxes, filters apply to the 4 planned transfers"
        }
      },
      dcVsErConfusion: {
        commonMisunderstanding: "Users often confuse DC and ER terminology",
        correct: {
          dc: "Discharge - Patients who were ADMITTED to hospital (wasAdmitted: true)",
          er: "Emergency Room - Patients who went to ER but were NOT admitted (wasAdmitted: false)"
        },
        whenUserAsksAboutDischarges: {
          theyMean: "DC only (admitted patients who were later discharged)",
          theyDontMean: "ER visits (patients who were never admitted)"
        },
        criticalNote: "When users ask about 'discharges within 30 days', they want DC data only"
      },
      percentageCalculationEdgeCases: {
        lockedTotals: {
          scenario: "User locks percentages to census data",
          effect: "All percentages use census as denominator regardless of filters",
          use_case: "Compare transfer rates against facility capacity"
        },
        specialComparisonSet: {
          scenario: "Priority filters change percentage calculation method",
          normal: "percentageBase = getPercentageByTotal(dbData)",
          withPriorityFilters: "percentageBase = filterTotal (filtered patient count)"
        },
        returnsAndHospitalsCards: {
          difference: "These cards use percentageAgainst instead of filterTotal",
          reason: "Different business logic for return rates and hospital destinations"
        }
      }
    };

    // CRITICAL: Precise Calculation Debugging Information
    const calculationDebugging = {
      dcErDataFunction: {
        description: "How DC/ER counts are calculated from patient data",
        sourceCode: `
        export async function dcErData(data, totalFilter) {
          // Use countBy to group by "wasAdmitted" values
          const DCERResult = _.countBy(data, "wasAdmitted");
          // DC count = patients where wasAdmitted === true
          // ER count = patients where wasAdmitted === false
          return [
            { id: "DC", label: "DC", total: DCERResult[true] || 0, ... },
            { id: "ER", label: "ER", total: DCERResult[false] || 0, ... }
          ];
        }`,
        criticalNote: "DC count is EXACT count of patients with wasAdmitted: true in the filtered dataset"
      },
      priorityFilterImpact: {
        description: "How priority filters affect DC/ER calculations",
        process: [
          "1. User clicks 'New Hospitalizations' checkbox",
          "2. patientFilterData is filtered to only New Hospitalization patients", 
          "3. dcErData() is called with this filtered patient list",
          "4. DC count = count of patients where wasAdmitted === true in filtered list",
          "5. This should be EXACT count, no rounding or scaling applied"
        ],
        commonError: "AI might be applying scaling or rounding when it shouldn't"
      },
      exactCalculationRules: {
        forDCCount: "Use exact count from DCERResult[true] - NO scaling, NO rounding",
        forPercentages: "Apply percentage calculation with proper base",
        forProjections: "Only apply scaling for projection calculations, NOT base counts"
      },
      debuggingSteps: {
        step1: "Identify the filtered patient dataset size",
        step2: "Count patients where wasAdmitted === true (this is DC count)",
        step3: "Count patients where wasAdmitted === false (this is ER count)",
        step4: "Verify DC + ER = total filtered patients",
        step5: "Apply percentage calculations using proper base"
      }
    };

    return {
      calculationFunctions,
      filterBusinessLogic,
      cardInteractionDetails,
      realWorldExamples,
      calculationDebugging
    };
  }, []); // Static content, no dependencies needed
};

export default useAICalculationContext;
